import { LiveAnnotations } from './live-annotations.js';
import { LoadingIndicator } from './loading-indicator.js';
import { SpectatorCamera } from './spectator-camera.js';
import { AssetCache } from './cache.js';
import { MapRenderer } from './renderer.js';
import { LiveAudioOutput } from './live-sounds.js';
import { LiveViewer } from './live-controller.js';
import { LiveMapLoader } from './live-map-loader.js';
import { LiveScoreboard } from './live-scoreboard.js';
import { Status } from './live-codec.js';
import { bindDebugControls } from './debug-controls.js';
import { bindPerformanceOverlay } from './performance-overlay.js';
import { fetchJson } from './http.js';
import { LiveDeathNotices } from './live-death-notices.js';
import { LiveChat } from './live-chat.js';
import { LiveVoice } from './live-voice.js';
import { LiveTargetId } from './live-target-id.js';

const $ = id => document.getElementById(id);
const embedded = $('viewer').dataset.embed === 'true';
const bootstrapUrl = id => `/stream/servers/${id}`;
let serverEntries = [];
const assetIndicator = new LoadingIndicator(), spectator = new SpectatorCamera();
let renderer, viewer, loader, selection, request, generation = 0, loading = false, latestStatus;
const diagnostic = message => {
    if ($('messages').childElementCount >= 200) return;
    const item = document.createElement('li'); item.textContent = message; $('messages').append(item);
    $('diagnostic-count').textContent = $('messages').childElementCount;
};
const audio = new LiveAudioOutput({ diagnostic });
$('sound-volume').value = String(audio.volume);
$('voice-volume').value = String(audio.voiceVolume);
function unlockAudio(event) {
    if (embedded) return;
    if ((event && !event.isTrusted) || audio.context?.state === 'running') return;
    void audio.unlock().then(() => voice.prepare()).catch(error => diagnostic(`Audio unavailable: ${error.message}`));
}
// Try autoplay where permitted, then retry synchronously during a real input
// gesture. Capture runs before the canvas click handler requests pointer lock.
document.addEventListener('pointerdown', unlockAudio, { capture: true });
document.addEventListener('pointerup', unlockAudio, { capture: true });
document.addEventListener('keydown', unlockAudio, { capture: true });
unlockAudio();
const cache = new AssetCache(diagnostic);
const chat = new LiveChat({ root: $('server-chat'), center: $('server-text'), canvas: $('canvas'), cache, diagnostic,
    onOpen: () => renderer?.controls.release(), onClose: () => renderer?.controls.capture(), onSubmit: text => viewer?.controller.sendChat(text) });
const annotations = new LiveAnnotations({ root: $('annotations'), cache, diagnostic, tokens: () => chat.tokens });
const deathNotices = new LiveDeathNotices({ root: $('death-notices'), cache, diagnostic });
const canChat = !embedded && $('viewer').dataset.viewerSignedIn === 'true';
let viewerAccess = false, nextPositionAt = -Infinity;
const scoreboard = new LiveScoreboard({ root: $('scoreboard'), canvas: $('canvas'), button: $('scoreboard-toggle'), cache, diagnostic });
const targetId = new LiveTargetId({ root: $('target-id'), cache, players: scoreboard.state.players, diagnostic });
const voice = new LiveVoice({ output: audio, root: $('voice-status'), players: scoreboard.state.players, cache, diagnostic });
const labels = {
    [Status.Disconnected]: 'Disconnected · waiting for the server to reconnect.',
    [Status.AwaitingSnapshot]: 'Recovering · waiting for a full update.',
    [Status.Loading]: 'Loading the current map…',
    [Status.Live]: 'Live',
    [Status.Stale]: 'Updates delayed · waiting for synchronization.',
    [Status.Idle]: 'Connecting · asking the server to resume updates.',
    [Status.MapUnavailable]: 'The current server map is unavailable.',
    [Status.MapChanging]: 'Server is changing maps…',
};
function showStatus(message) { $('status').textContent = message; }
function refreshStreamDisplay() {
    if (!latestStatus) return;
    const eligible = latestStatus.status === Status.Live && !loading;
    if (!eligible) assetIndicator.reset();
    const lateLoading = eligible && assetIndicator.update(renderer?.assetsLoading || loader?.assetsLoading, performance.now());
    const label = latestStatus.unavailable ? latestStatus.diagnostic : latestStatus.replaced ? 'This viewer session moved to another tab. Select Retry to take it back.' : lateLoading ? 'Live · loading assets…' : labels[latestStatus.status] || 'Connecting…';
    if ($('server-status').textContent !== label) $('server-status').textContent = label;
    if (!loading && $('status').textContent !== label) showStatus(label);
    $('status').classList.toggle('live-status-loading', lateLoading || [Status.Loading, Status.MapChanging].includes(latestStatus.status));
}
function performanceStatus({ worker, delivery, scene }) {
    if (renderer) renderer.liveMetrics = { worker, delivery };
    const ms = value => Number.isFinite(value) ? `${value.toFixed(2)} ms` : 'waiting';
    $('live-performance').textContent = `Live work p95: ${ms(scene?.timings.frameWorkMs.p95)} per rendered frame (drawing excluded). ` +
        `Worker p95: ${ms(worker?.timings.frameMs.p95)}. Delivery p95: ${ms(delivery.deliveryMs.p95)}. ` +
        `Entities: ${scene?.entities ?? 0}; placeholders: ${scene?.placeholders ?? 0}; loading: ${(scene?.pendingModels ?? 0) + (scene?.modelsInFlight ?? 0)}.`;
    const age = time => time == null ? 'none' : `${Math.max(0, (performance.now() - time) / 1000).toFixed(1)} s ago`;
    const sounds = renderer?.liveScene?.sounds;
    const sprays = renderer?.liveScene?.sprays.diagnostics();
    if ($('live-spray-diagnostics')) $('live-spray-diagnostics').textContent = sprays?.received
        ? `Player sprays: ${sprays.received} received; ${sprays.projected} projected; ${sprays.loading} loading; ${sprays.waiting} awaiting surface; ${sprays.failed} failed.`
        : 'Player sprays: no spray records received from the server for this map.';
    const playing = sounds ? [...sounds.entries.values()].filter(entry => entry.media && !entry.media.paused).length : 0;
    $('live-audio-performance').textContent = `Audio: ${audio.context?.state ?? 'waiting for permission'}; ` +
        `sound ${audio.enabled ? 'enabled' : 'muted'}; stream ${audio.allowed ? 'allowed' : 'paused'}; ` +
        `output ${Math.round((audio.master?.gain.value ?? 0) * 100)}%; playing sounds ${playing}. ` +
        `Voice packets ${voice.receivedPackets ?? 0}, last received ${age(voice.lastReceivedAt)}, last decoded ${age(voice.lastDecodedAt)}.`;
}
function streamStatus(message) {
    latestStatus = message;
    chat.setEnabled(canChat && viewerAccess && message.status === Status.Live);
    audio.setLive(!document.hidden && message.status === Status.Live);
    if (message.status !== Status.Live) renderer?.liveScene?.sounds.silence();
    if (message.status !== Status.Live) { targetId.reset(); voice.reset(); }
    $('status').classList.toggle('live-status-centered', message.status === Status.MapChanging);
    if (message.status === Status.MapChanging) {
        loader?.cancel(); loading = false; $('cancel').hidden = true;
        $('map-name').textContent = 'Changing map'; scoreboard.reset(); targetId.reset();
    }
    scoreboard.metadata({ mapName: message.map?.name, live: message.status === Status.Live, status: message.status === Status.Live ? '' : labels[message.status] });
    if (message.status === Status.MapUnavailable && !message.map?.id) { loader?.cancel(); loading = false; }
    if (message.map?.name) $('map-name').textContent = message.map.name;
    if (message.diagnostic) diagnostic(message.diagnostic);
    refreshStreamDisplay();
    $('retry').hidden = ![Status.MapUnavailable, Status.Disconnected, Status.Stale].includes(message.status);
    if (message.status === Status.MapUnavailable) $('cancel').hidden = true;
}
try {
    if (!$('canvas').getContext('webgl2')) throw new Error('WebGL2 is unavailable. Enable hardware acceleration and reload.');
    renderer = new MapRenderer($('canvas'), diagnostic);
    renderer.onSpectatorCycle = direction => { if (latestStatus?.status === Status.Live && !loading) spectator.cycle(renderer, renderer.liveScene, direction); };
    renderer.audioOutput = audio;
    renderer.viewerSteamId = $('viewer').dataset.viewerSteamId;
    renderer.presentationAudio = voice;
    // The providers return null outside the opt-in measurement session.
    voice.metrics = scoreboard.metrics = () => renderer.performanceOverlay?.details;
    renderer.onLiveFrame = scene => {
        const metrics = renderer.performanceOverlay?.details, started = metrics ? performance.now() : 0;
        if (!loading && latestStatus?.status === Status.Live) refreshStreamDisplay();
        if (scene) { const time = scene.timeline.animationTime(scene.now()); chat.frame(time); deathNotices.frame(time); annotations.frame(time, scene, renderer.camera); }
        if (!document.hidden && latestStatus?.status === Status.Live && $('scoreboard').hidden) targetId.frame(scene, renderer.camera, renderer.visibility);
        else targetId.reset();
        metrics?.record('hud', performance.now() - started);
    };
    renderer.onLiveRendered = scene => {
        const now = performance.now();
        if (viewerAccess && !loading && scene?.owned && scene.epoch === viewer?.controller.epoch &&
            latestStatus?.status === Status.Live && now >= nextPositionAt) {
            // Keep the 50 ms phase across render jitter; never burst after a stall.
            nextPositionAt = now - nextPositionAt < 50 ? nextPositionAt + 50 : now + 50;
            // Renderer pitch is positive-up radians; Source pitch is positive-down degrees.
            const yaw = ((renderer.yaw * 180 / Math.PI % 360) + 540) % 360 - 180;
            viewer.controller.sendPosition(renderer.camera.position.toArray(), [-renderer.pitch * 180 / Math.PI, yaw, 0]);
        }
    };
    loader = new LiveMapLoader({ renderer, cache, onProgress: showStatus, onDiagnostic: diagnostic,
        onFailure: error => { viewer?.disconnect(); loader.cancel(); loading = false; showStatus(error.message); $('retry').hidden = false; },
    });
    viewer = new LiveViewer({ renderer, onStatus: streamStatus, onMetrics: performanceStatus,
        onVoice: payload => voice.receive(payload),
        onViewerAccess: enabled => { viewerAccess = enabled; chat.setEnabled(canChat && enabled && latestStatus?.status === Status.Live); },
        onEntities: message => {
            const metrics = renderer.performanceOverlay?.details, started = metrics ? performance.now() : 0;
            scoreboard.apply(message); chat.apply(message); deathNotices.apply(message); annotations.apply(message);
            metrics?.record('hud', performance.now() - started);
        }, onReset: () => { renderer.performanceOverlay?.reset(); renderer.liveMetrics = null; scoreboard.reset(); targetId.reset(); chat.reset(true); deathNotices.reset(); annotations.reset(); voice.reset(); },
        loadMap: async (map, context) => {
            void chat.load(map.assetSet); void deathNotices.load(map.assetSet); void annotations.load(map.assetSet);
            void scoreboard.load(map.assetSet);
            void targetId.load(map.assetSet);
            void voice.load(map.assetSet);
            loading = true; $('cancel').hidden = false; $('retry').hidden = true;
            try { return await loader.load(map, context); }
            finally {
                if (!context.signal.aborted) {
                    loading = false; $('cancel').hidden = true;
                    if (latestStatus) streamStatus(latestStatus);
                }
            }
        },
    });
} catch (error) { showStatus(error.message); }
bindDebugControls(renderer);
bindPerformanceOverlay(renderer);

function serverAddress(id, name, url) {
    if (embedded) return;
    const address = new URL(location.href);
    address.pathname = id ? (url || `/${id}`) : '/';
    address.searchParams.delete('server'); address.hash = '';
    history.replaceState(null, '', address);
    document.title = `${id ? name : 'Live'} - Saxton Hell Live`;
}
async function selectServer(id) {
    if (selection !== id) chat.reset();
    const server = serverEntries.find(entry => entry.id === id);
    serverAddress(id, server?.name ?? 'Server', server?.url);
    const own = ++generation;
    request?.abort(); request = new AbortController();
    viewer?.disconnect(); loader?.cancel(); loading = false; latestStatus = null;
    scoreboard.reset(); targetId.reset(); scoreboard.metadata({ serverName: $('server').selectedOptions[0]?.textContent ?? '', mapName: '', status: 'Waiting for player data…' });
    audio.setLive(false);
    $('status').classList.remove('live-status-centered', 'live-status-loading');
    assetIndicator.reset(); spectator.key = null;
    selection = id; $('retry').hidden = true; $('cancel').hidden = true;
    $('join-server').hidden = !id;
    if (id) $('join-server').href = `https://saxtonhell.com/connect/${id}`;
    else $('join-server').removeAttribute('href');
    $('welcome').hidden = Boolean(id); $('messages').replaceChildren(); $('diagnostic-count').textContent = '0';
    $('live-performance').textContent = 'Timing data is available after live updates begin.';
    $('live-audio-performance').textContent = 'Audio diagnostics are available after live updates begin.';
    $('map-name').textContent = 'Waiting for current map';
    if (!id) { $('server-status').textContent = 'Select a server to watch.'; showStatus('Select a server to watch.'); return; }
    if (!viewer) return;
    $('server-status').textContent = 'Connecting…'; showStatus('Loading server details…');
    try {
        const response = await fetch(bootstrapUrl(id), { signal: request.signal, cache: 'no-store' });
        if (!response.ok) throw new Error(response.status === 503 ? 'Live spectating is currently unavailable.' : `Server unavailable (${response.status}).`);
        const bootstrap = await response.json();
        if (own !== generation) return;
        if (bootstrap.serverName) serverAddress(id, bootstrap.serverName, bootstrap.url);
        $('server-status').textContent = bootstrap.cachedMap ? `Last queried map: ${bootstrap.cachedMap} · waiting for live updates.` : 'Waiting for live updates.';
        // Cached metadata is only a loading hint. The controller accepts entities solely
        // after loading the map announced by the stream's authoritative epoch.
        if (bootstrap.map?.id) {
            loading = true; $('cancel').hidden = false;
            loader.load(bootstrap.map, { signal: request.signal }).then(() => {
                if (own === generation && !viewer.controller.loadingMap) {
                    loading = false; $('cancel').hidden = true;
                    if (latestStatus) streamStatus(latestStatus);
                    else showStatus('Map loaded · waiting for live updates.');
                }
            }).catch(error => {
                if (own === generation && error.name !== 'AbortError' && !viewer.controller.loadingMap) {
                    loading = false; $('cancel').hidden = true; diagnostic(error.message);
                    showStatus('Cached map unavailable · waiting for the server’s current map.');
                }
            });
        }
        viewer.selectServer(id);
    } catch (error) {
        if (own !== generation || error.name === 'AbortError') return;
        $('server-status').textContent = error.message; showStatus(error.message); $('retry').hidden = false;
    }
}
async function servers() {
    if (embedded) {
        await selectServer(Number($('viewer').dataset.serverId));
        return;
    }
    $('reload-servers').hidden = true;
    try {
        const response = await fetch('/live-servers', { cache: 'no-store' });
        if (!response.ok) throw new Error(response.status === 503 ? 'Live spectating is currently unavailable.' : 'The server list could not be loaded.');
        const entries = serverEntries = await response.json();
        const cards = $('welcome-servers'); cards.replaceChildren();
        for (const server of entries) {
            const card = document.createElement('button'); card.type = 'button'; card.className = 'live-server-card'; card.disabled = !viewer;
            const name = document.createElement('strong'), details = document.createElement('span'), audience = document.createElement('span');
            audience.dataset.viewerCount = String(server.id); audience.textContent = 'Viewer count unavailable';
            name.textContent = server.name; details.textContent = `${server.map || 'Map unavailable'} · ${server.players == null ? 'Player count unavailable' : `${server.players} / ${server.maxPlayers ?? '?'} players`}`;
            if (/^[a-z-]+$/.test(server.icon ?? '')) {
                const icon = document.createElement('img'); icon.src = `/_content/SaxtonHell.Shared/images/servers/${server.icon}.png`; icon.alt = ''; icon.width = icon.height = 24; name.prepend(icon);
            }
            card.append(name, details, audience); card.onclick = () => { $('server').value = String(server.id); void selectServer(server.id); }; cards.append(card);
        }
        void refreshViewerCounts();
        $('server').replaceChildren(new Option('Select a server', ''));
        for (const server of entries) $('server').append(new Option(server.name, String(server.id)));
        $('server').disabled = !viewer || entries.length === 0;
        $('server-status').textContent = entries.length ? 'Select a server to watch.' : 'No servers are available.';
        const path = location.pathname.replace(/\/$/, '');
        let selected = entries.find(server => server.url === path || server.id === Number(path.split('/')[1]));
        const directId = Number($('viewer').dataset.serverId);
        if (!selected && path && directId > 0) {
            // Unlisted servers are resolved by the page route, never added to public discovery.
            const response = await fetch(bootstrapUrl(directId), { cache: 'no-store' });
            if (!response.ok) throw new Error(`Server unavailable (${response.status}).`);
            const direct = await response.json();
            selected = { id: direct.serverId, name: direct.serverName, url: direct.url };
            serverEntries.push(selected); $('server').append(new Option(selected.name, String(selected.id)));
            $('server').disabled = !viewer;
        }
        if (selected) { $('server').value = String(selected.id); await selectServer(selected.id); }
        else showStatus($('server-status').textContent);
    } catch (error) { $('server-status').textContent = error.message; showStatus(error.message); $('reload-servers').hidden = false; }
}
let viewerCountsRequest;
async function refreshViewerCounts() {
    if (document.hidden || viewerCountsRequest) return;
    const request = viewerCountsRequest = new AbortController();
    try {
        const counts = await fetchJson('/stream/viewers', { cache: 'no-store', signal: request.signal });
        for (const element of $('welcome-servers').querySelectorAll('[data-viewer-count]')) {
            const count = counts[element.dataset.viewerCount];
            element.textContent = Number.isSafeInteger(count) && count >= 0 ? `${count} live ${count === 1 ? 'viewer' : 'viewers'}` : 'Viewer count unavailable';
        }
    } catch {
        for (const element of $('welcome-servers').querySelectorAll('[data-viewer-count]')) element.textContent = 'Viewer count unavailable';
    } finally { if (viewerCountsRequest === request) viewerCountsRequest = null; }
}
const viewerCountsTimer = setInterval(() => { if (!$('welcome').hidden) void refreshViewerCounts(); }, 15000);
document.addEventListener('visibilitychange', () => { if (!document.hidden && !$('welcome').hidden) void refreshViewerCounts(); });
window.addEventListener('pagehide', () => { clearInterval(viewerCountsTimer); viewerCountsRequest?.abort(); });
$('server').onchange = () => void selectServer(Number($('server').value));
$('reload-servers').onclick = servers;
$('cancel').onclick = () => {
    generation++; request?.abort(); viewer?.disconnect(); loader?.cancel(); scoreboard.reset(); targetId.reset(); loading = false;
    showStatus('Viewing paused. Retry to reconnect.'); $('server-status').textContent = 'Paused';
    $('cancel').hidden = true; $('retry').hidden = false;
};
$('retry').onclick = () => selection && selectServer(selection);
function updateVoiceSubscription() {
    audio.setVoiceEnabled(voice.supported && voice.enabled);
    viewer?.controller.setVoiceEnabled(voice.supported && voice.enabled && !document.hidden);
}
voice.enabled = audio.voiceVolume > 0;
void voice.initialize().then(supported => {
    $('voice-volume').disabled = !supported;
    if (!supported) {
        $('voice-volume').title = 'Voice unavailable in this browser';
        diagnostic('Voice unavailable in this browser');
    }
    updateVoiceSubscription(); void voice.prepare();
});
$('sound-volume').oninput = event => audio.setVolume(Number(event.target.value));
$('voice-volume').oninput = event => {
    audio.setVoiceVolume(Number(event.target.value));
    const enabled = audio.voiceVolume > 0;
    if (voice.enabled !== enabled) { voice.enabled = enabled; voice.reset(); updateVoiceSubscription(); }
};
document.addEventListener('visibilitychange', () => {
    voice.reset(); updateVoiceSubscription();
    audio.setLive(!document.hidden && latestStatus?.status === Status.Live);
    if (document.hidden) renderer?.liveScene?.sounds.silence();
});
$('reset').onclick = () => loader?.sceneData && renderer.reset(loader.sceneData.entities, loader.sceneData.models);
$('speed').oninput = event => { if (renderer) renderer.speed = Number(event.target.value); };
$('sensitivity').oninput = event => {
    const value = Number(event.target.value); if (renderer) renderer.sensitivity = value;
    $('sensitivity-value').textContent = `${value.toFixed(1)}×`;
};
$('quality').onchange = event => renderer?.renderer.setPixelRatio(Number(event.target.value));
$('share').onclick = async () => {
    try { await navigator.clipboard.writeText(location.href); showStatus('Live server link copied.'); }
    catch { showStatus('Copy the address bar to share this server.'); }
};
async function usage() { try { $('cache-size').textContent = `${((await cache.usage()) / 1048576).toFixed(1)} MiB saved on this browser`; } catch {} }
$('clear-cache').onclick = async () => { await cache.clear(); await usage(); };
$('canvas').addEventListener('webglcontextlost', event => { event.preventDefault(); $('cancel').onclick(); showStatus('Graphics context lost. Waiting for recovery…'); });
$('canvas').addEventListener('webglcontextrestored', () => { if (selection) void selectServer(selection); });
window.addEventListener('pagehide', () => { generation++; request?.abort(); viewer?.dispose(); loader?.dispose(); scoreboard.dispose(); chat.dispose(); deathNotices.dispose(); annotations.dispose(); targetId.dispose(); voice.dispose(); audio.dispose(); renderer?.dispose?.(); });
window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
void servers(); void usage();
